import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { QuestionCard } from '@/components/ui/question-card';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, XCircle, Minus } from 'lucide-react';
import { TemplateContent } from '../../../src/schemas/templateContent';

interface TemplateRendererProps {
  content: TemplateContent;
  readOnly?: boolean;
}

export function TemplateRenderer({ content, readOnly = false }: TemplateRendererProps) {
  const renderMetaField = (field: any) => {
    const value = field.readOnly ? 
      `[Auto: ${field.label}]` : 
      `[${field.type === 'textarea' ? 'Text Area' : field.type === 'number' ? 'Number' : 'Text'} Input]`;

    const commonProps = {
      disabled: readOnly || field.readOnly,
      className: readOnly || field.readOnly ? 'bg-muted/50 cursor-not-allowed' : 'bg-muted/50',
      'data-testid': `preview-meta-${field.key}`
    };

    switch (field.type) {
      case 'textarea':
        return <Textarea placeholder={value} {...commonProps} />;
      case 'number':
        return <Input type="number" placeholder={value} {...commonProps} />;
      default:
        return <Input placeholder={value} {...commonProps} />;
    }
  };

  const renderTemplateItem = (item: any) => {
    switch (item.type) {
      case 'section':
        return (
          <div key={item.id} className="py-4">
            <h3 className="text-lg font-semibold text-foreground border-b pb-2">
              {item.label}
            </h3>
          </div>
        );

      case 'ynna_actions_review':
        return (
          <QuestionCard
            key={item.id}
            id={item.id}
            label={item.label}
            required={item.required}
            status="idle"
          >
            {/* Response Options - Yes/No/N/A */}
            <div className="flex flex-wrap gap-3">
              {['Yes', 'No', 'N/A'].map((option) => (
                <Button
                  key={option}
                  variant="outline"
                  size="lg"
                  disabled={readOnly}
                  className="flex items-center gap-2.5 min-h-[44px] px-6 py-3 text-base font-medium cursor-not-allowed"
                  data-testid={`preview-response-${item.id}-${option.toLowerCase()}`}
                >
                  {option === 'Yes' && <CheckCircle className="w-5 h-5" />}
                  {option === 'No' && <XCircle className="w-5 h-5" />}
                  {option === 'N/A' && <Minus className="w-5 h-5" />}
                  <span>{option}</span>
                </Button>
              ))}
            </div>
            
            {/* Actions Section indicator */}
            {item.actions_enabled && (
              <div className="pt-4 border-t border-border/60">
                <div className="text-sm text-muted-foreground italic">
                  Actions section will be available when completing this audit
                </div>
              </div>
            )}
            
            {/* Score indicator for this item */}
            {item.score?.enabled && (
              <div className="text-[13px] text-muted-foreground pt-3 flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  Scoring enabled (weight: {item.score.weight || 1})
                </Badge>
              </div>
            )}
          </QuestionCard>
        );

      case 'dropdown':
        return (
          <QuestionCard
            key={item.id}
            id={item.id}
            label={item.label}
            required={item.required}
            status="idle"
          >
            {/* Dropdown Selection */}
            <div className="space-y-2">
              <div className={`w-full border rounded-md px-3 py-2 text-sm ${readOnly ? 'bg-muted/50 cursor-not-allowed' : 'bg-background'}`}>
                <span className="text-muted-foreground">Select an option...</span>
              </div>
              {readOnly && item.config?.choices && item.config.choices.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Options: {item.config.choices.map((c: any) => c.label).join(', ')}
                </div>
              )}
            </div>
            
            {/* Actions Section indicator */}
            {item.actions_enabled && (
              <div className="pt-4 border-t border-border/60">
                <div className="text-sm text-muted-foreground italic">
                  Actions section will be available when completing this audit
                </div>
              </div>
            )}
          </QuestionCard>
        );

      case 'text':
        return (
          <div key={item.id} className="space-y-2">
            <Label>
              {item.label}
              {item.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input 
              placeholder="Enter text..." 
              disabled={readOnly}
              className={readOnly ? 'cursor-not-allowed' : ''}
              data-testid={`preview-text-${item.id}`} 
            />
            {item.actions_enabled && (
              <div className="text-xs text-muted-foreground italic">
                Actions section available when completing
              </div>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={item.id} className="space-y-2">
            <Label>
              {item.label}
              {item.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Textarea 
              placeholder="Enter text..." 
              disabled={readOnly}
              className={readOnly ? 'cursor-not-allowed' : ''}
              data-testid={`preview-textarea-${item.id}`} 
            />
            {item.actions_enabled && (
              <div className="text-xs text-muted-foreground italic">
                Actions section available when completing
              </div>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={item.id} className="space-y-2">
            <Label>
              {item.label}
              {item.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input 
              type="number" 
              placeholder="Enter number..." 
              disabled={readOnly}
              className={readOnly ? 'cursor-not-allowed' : ''}
              data-testid={`preview-number-${item.id}`} 
            />
            {item.actions_enabled && (
              <div className="text-xs text-muted-foreground italic">
                Actions section available when completing
              </div>
            )}
          </div>
        );

      default:
        return (
          <div key={item.id} className="p-2 border border-dashed rounded text-muted-foreground">
            Unknown item type: {item.type}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Meta Fields Section */}
      {content.metaFields.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-foreground">Audit Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {content.metaFields.map((field, index) => (
              <div key={index}>
                <Label>{field.label}</Label>
                <div className="mt-1">
                  {renderMetaField(field)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {content.metaFields.length > 0 && <Separator />}

      {/* Template Items */}
      <div className="space-y-6">
        <h3 className="font-semibold text-foreground">Audit Questions</h3>
        {content.items.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <p>No items in this template</p>
          </div>
        ) : (
          content.items.map(renderTemplateItem)
        )}
      </div>

      {/* Signoff Section */}
      {content.signoff.length > 0 && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Sign Off</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.signoff.map((field, index) => (
                <div key={index}>
                  <Label>{field.label}</Label>
                  <div className="mt-1">
                    {field.type === 'auto' ? (
                      <Input
                        placeholder={`[Auto: ${field.label}]`}
                        disabled
                        className="bg-muted/50 cursor-not-allowed"
                        data-testid={`preview-signoff-${field.key}`}
                      />
                    ) : (
                      <Input
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                        disabled={readOnly}
                        className={readOnly ? 'cursor-not-allowed' : ''}
                        data-testid={`preview-signoff-${field.key}`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
